import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { TnCardComponent, TnIconComponent, TnProgressBarComponent } from '@truenas/ui-components';
import { combineLatest, Observable, of } from 'rxjs';
import {
  delay,
  filter, map, switchMap, take, pairwise, distinctUntilChanged,
} from 'rxjs/operators';
import { WINDOW } from 'app/helpers/window.helper';
import { AuthService } from 'app/modules/auth/auth.service';
import { DialogService } from 'app/modules/dialog/dialog.service';
import { CopyrightLineComponent } from 'app/modules/layout/copyright-line/copyright-line.component';
import { TranslatedString } from 'app/modules/translate/translate.helper';
import { DisconnectedMessageComponent } from 'app/pages/signin/disconnected-message/disconnected-message.component';
import { SetAdminPasswordFormComponent } from 'app/pages/signin/set-admin-password-form/set-admin-password-form.component';
import { SigninFormComponent } from 'app/pages/signin/signin-form/signin-form.component';
import { SigninStore } from 'app/pages/signin/store/signin.store';
import { TrueCommandStatusComponent } from 'app/pages/signin/true-command-status/true-command-status.component';
import { TokenLastUsedService } from 'app/services/token-last-used.service';
import { WebSocketStatusService } from 'app/services/websocket-status.service';

@Component({
  selector: 'ix-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TnProgressBarComponent,
    TnCardComponent,
    TnIconComponent,
    SigninFormComponent,
    SetAdminPasswordFormComponent,
    TrueCommandStatusComponent,
    DisconnectedMessageComponent,
    AsyncPipe,
    NgTemplateOutlet,
    TranslateModule,
    CopyrightLineComponent,
  ],
  providers: [SigninStore],
})
export class SigninComponent implements OnInit {
  private wsStatus = inject(WebSocketStatusService);
  private signinStore = inject(SigninStore);
  private dialog = inject(DialogService);
  private authService = inject(AuthService);
  private tokenLastUsedService = inject(TokenLastUsedService);
  private window = inject<Window>(WINDOW);
  private destroyRef = inject(DestroyRef);

  /** Read live: the token can be cleared while this page is up, as a refused token login does. */
  protected get hasAuthToken(): boolean {
    return this.authService.hasAuthToken;
  }

  protected isTokenWithinTimeline$ = this.tokenLastUsedService.isTokenWithinTimeline$;

  readonly wasAdminSet$ = this.signinStore.wasAdminSet$;
  readonly isLoggingIn$ = this.signinStore.isLoggingIn$;
  readonly canLogin$ = this.signinStore.canLogin$;
  readonly isConnected$ = this.wsStatus.isConnected$;
  isConnectedDelayed$: Observable<boolean> = of(null).pipe(
    delay(1000),
    switchMap(() => this.isConnected$),
  );

  readonly hasLoadingIndicator$ = combineLatest([
    this.signinStore.isLoading$.pipe(distinctUntilChanged()),
    this.wsStatus.isConnected$.pipe(distinctUntilChanged()),
    this.isTokenWithinTimeline$.pipe(distinctUntilChanged()),
  ]).pipe(
    map(([isLoading, isConnected, isTokenWithinTimeline]) => {
      return isLoading || !isConnected || (isTokenWithinTimeline && this.hasAuthToken);
    }),
    distinctUntilChanged(),
  );

  protected hasLoadingIndicator = toSignal(this.hasLoadingIndicator$, { initialValue: false });

  constructor() {
    this.wsStatus.isFailoverRestart$
      .pipe(
        filter(Boolean),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.signinStore.init();
        this.wsStatus.setFailoverStatus(false);
      });

    this.signinStore.loginBanner$.pipe(
      filter(Boolean),
      filter(() => this.window.sessionStorage.getItem('loginBannerDismissed') !== 'true'),
      switchMap((text) => {
        return this.dialog.fullScreenDialog({
          message: text as TranslatedString,
          showClose: true,
          pre: true,
        }).pipe(take(1));
      }),
      filter(Boolean),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.focusFirstInput();
    });

    this.signinStore.isLoggingIn$.pipe(
      filter(Boolean),
      // Let the message render before reaching for it.
      delay(0),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.window.document.querySelector<HTMLElement>('.logging-in')?.focus();
    });

    this.hasLoadingIndicator$
      .pipe(
        delay(0),
        pairwise(),
        filter(([prev, curr]) => prev && !curr),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.focusFirstInput();
      });
  }

  ngOnInit(): void {
    this.isConnected$
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Check if this is after a system update - reload page to get latest UI
        const isUpdateRestart = this.window.sessionStorage.getItem('updateCompleted') === 'true';
        if (isUpdateRestart) {
          this.window.sessionStorage.removeItem('updateCompleted');
          this.window.location.reload();
          return;
        }

        this.signinStore.init();
      });
  }

  private focusFirstInput(): void {
    if (this.window.document.querySelector('ix-full-screen-dialog')) {
      return;
    }

    this.window.document.querySelector<HTMLElement>('input')?.focus();
  }
}
